import { useRef } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { DEFAULT_CONTEXT_VALUE } from '../constants';
import { TaskCenterContext } from '../types';
import { isSameSelection } from '../utils/is-same-selection';

export const TaskCenterProviderContext = createContext<TaskCenterContext>(DEFAULT_CONTEXT_VALUE);

export const useTaskCenterStore = <T extends (keyof TaskCenterContext)[]>(values: T) => {
  type Selection = Pick<TaskCenterContext, T[number]>;
  const lastSelection = useRef<Selection>();

  // useContextSelector compares the selected value with Object.is on every render and forces another render when it
  // differs. Returning a new object each time therefore re-renders every consumer in an endless loop, so the previous
  // object is reused while all of the selected values are unchanged.
  return useContextSelector(TaskCenterProviderContext, (context) => {
    const nextSelection = values.reduce((acc, key) => ({ ...acc, [key]: context?.[key] }), {} as Selection);
    const previousSelection = lastSelection.current;

    if (previousSelection && isSameSelection(previousSelection, nextSelection)) {
      return previousSelection;
    }

    lastSelection.current = nextSelection;
    return nextSelection;
  });
};
